<template>
  <!-- Mobile/Tablet Header (visible below 1024px) -->
  <header
    class="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-50 flex items-center justify-between px-4">
    <AppLogo />
    <Button variant="outline" size="icon" @click="toggleMobileMenu" class="h-10 w-10 py-2">
      <Menu class="h-5 w-5" />
    </Button>
  </header>

  <!-- Mobile/Tablet Drawer Overlay -->
  <Transition name="fade">
    <div v-if="isMobileMenuOpen" class="lg:hidden fixed inset-0 bg-black/50 z-40" @click="toggleMobileMenu"></div>
  </Transition>

  <!-- Mobile/Tablet Drawer -->
  <Transition :name="isRTL ? 'slide-right' : 'slide-left'">
    <aside v-if="isMobileMenuOpen" :class="cn(
      'lg:hidden fixed top-0 bottom-0 w-64 bg-card z-50 shadow-xl',
      {
        'right-0': isRTL,
        'left-0': !isRTL,
      }
    )
      ">
      <nav class="h-full flex flex-col">
        <!-- Mobile/Tablet Drawer Header -->
        <div class="flex items-center justify-between h-16 px-4 border-b">
          <AppLogo />
          <Button variant="ghost" size="icon" @click="toggleMobileMenu" class="h-8 w-8">
            <X class="h-5 w-5" />
          </Button>
        </div>

        <!-- Mobile/Tablet Navigation links -->
        <ScrollArea class="flex-1">
          <ul class="px-2 py-2">
            <template v-for="(link, index) in filteredLinks" :key="index">
              <!-- Divider (standalone) -->
              <li v-if="link.type === 'divider'" class="my-3">
                <div class="h-px bg-border"></div>
              </li>
              <!-- Section Header with Divider Line -->
              <li v-else-if="link.type === 'header'" class="my-3 px-2">
                <div class="relative flex items-center">
                  <div class="flex-grow border-t border-border"></div>
                  <span
                    class="flex-shrink mx-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {{ $t(link.label) }}
                  </span>
                  <div class="flex-grow border-t border-border"></div>
                </div>
              </li>
              <!-- Regular Link -->
              <li v-else class="pb-1" @click="handleMobileLinkClick">
                <NavigationClosed :navigator="link" />
              </li>
            </template>
          </ul>
        </ScrollArea>

        <!-- Mobile/Tablet User section -->
        <div class="mt-auto p-2 border-t">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <NavigationUser />
              <span :class="['ml-2 capitalize select-none', { 'ml-0 mr-2': isRTL }]">
                {{ authStore.user?.name || "User" }}
              </span>
            </div>
          </div>
        </div>
      </nav>
    </aside>
  </Transition>

  <!-- Desktop Sidebar (visible from 1024px and up) -->
  <aside :class="cn(
    `hidden lg:flex h-full relative border-r-1 bg-card transition-all duration-300 shadow flex-shrink-0 ${isCollapsed ? 'w-14' : 'w-64'}`,
    {
      'border-l border-r-0': isRTL,
      'border-r border-l-0': !isRTL,
    }
  )
    ">
    <nav class="h-full flex flex-col justify-between w-full" :class="{
      'border-l shadow-sm': !isRTL,
      'border-r shadow-sm': isRTL,
    }">
      <!-- Logo or Icon -->
      <div :class="cn(
        `flex items-center h-16 ${isCollapsed ? 'justify-center' : 'justify-between px-4'}`
      )
        ">
        <AppLogo :show-title="!isCollapsed" />
        <Button variant="outline" size="icon" @click="toggleCollapse" :class="cn(
          'absolute top-10 hidden rounded-full lg:inline-flex z-20 w-7 h-7 p-0',
          {
            '-left-3': isRTL,
            '-right-3': !isRTL,
          }
        )
          ">
          <ChevronLeft :class="cn('h-4 w-4', {
            'rotate-180': !isRTL ? isCollapsed : !isCollapsed,
            'rotate-0': !isRTL ? !isCollapsed : isCollapsed,
          })
            " />
        </Button>
      </div>

      <!-- Navigation links -->
      <ScrollArea class="flex-1">
        <ul class="px-2 py-2">
          <template v-for="(link, index) in filteredLinks" :key="index">
            <!-- Divider (standalone) -->
            <li v-if="link.type === 'divider'" class="my-3">
              <div class="h-px bg-border"></div>
            </li>
            <!-- Section Header with Divider Line -->
            <li v-else-if="link.type === 'header'" class="my-3">
              <!-- Expanded: Show text on divider line -->
              <div v-if="!isCollapsed" class="relative flex items-center px-2">
                <div class="flex-grow border-t border-border"></div>
                <span
                  class="flex-shrink mx-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  {{ $t(link.label) }}
                </span>
                <div class="flex-grow border-t border-border"></div>
              </div>
              <!-- Collapsed: Just show divider -->
              <div v-else class="h-px bg-border"></div>
            </li>
            <!-- Regular Link -->
            <li v-else :class="{
              flex: isCollapsed,
              'justify-start': !isRTL,
              'justify-end': isRTL,
            }" class="pb-1">
              <NavigationOpened v-if="isCollapsed" :navigator="link" />
              <NavigationClosed v-else :navigator="link" />
            </li>
          </template>
        </ul>
      </ScrollArea>

      <!-- User section -->
      <div class="mt-auto p-2 z-20 border-t">
        <div class="flex items-center justify-between">
          <div v-if="!isCollapsed" class="flex items-center">
            <NavigationUser />
            <span v-if="!isCollapsed" :class="['ml-2 capitalize select-none', { 'ml-0 mr-2': isRTL }]">
              {{ authStore.user?.name || "User" }}
            </span>
          </div>
          <NavigationUser v-else trigger="more" />
        </div>
      </div>
    </nav>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Button } from "@/shared/components/ui/button/index";
import ScrollArea from "@/shared/components/ui/scroll-area/ScrollArea.vue";
import { ChevronLeft, Menu, X } from "lucide-vue-next";
import { cn } from "@/lib/utils";
import AppLogo from "./AppLogo.vue";
import NavigationOpened from "./NavigationOpened.vue";
import NavigationClosed from "./NavigationClosed.vue";
import NavigationUser from "./NavigationUser.vue";
import roleBasedSidebarLinks from "@/shared/constants";
import { NavigationModel } from "@/types/models/Navigation";
import { useAuthStore } from "@/core/auth/auth";
import { usePermission } from "@/core/composables/usePermission";

withDefaults(
  defineProps<{
    navigators?: NavigationModel[];
  }>(),
  { navigators: () => [] }
);

const isCollapsed = defineModel<boolean>("collapsed", { default: false });

const { locale } = useI18n();
const route = useRoute();
const authStore = useAuthStore();
const { hasPermission } = usePermission();
const isMobileMenuOpen = ref(false);

const isRTL = computed(() => locale.value === "fa");

const filteredLinks = computed(() => {
  const userRole = authStore.user?.role || "";
  
  // First, map and filter children for each link
  const linksWithFilteredChildren = roleBasedSidebarLinks
    .map((link) => {
      if (link.children && link.children.length > 0) {
        const visibleChildren = link.children.filter((child: any) => {
          if (!child.show) return false;

          if (child.permission) {
            return hasPermission(child.permission);
          }

          return child.access.includes(userRole);
        });
        return {
          ...link,
          children: visibleChildren
        };
      }
      return link;
    });

  // Now, filter the top-level links and headers
  const result: NavigationModel[] = [];
  
  for (let i = 0; i < linksWithFilteredChildren.length; i++) {
    const link = linksWithFilteredChildren[i];

    if (!link.show) continue;

    // If it is a header, only show it if there is at least one visible regular link
    // before the next header or the end of the list
    if (link.type === 'header') {
      let hasVisibleContent = false;
      for (let j = i + 1; j < linksWithFilteredChildren.length; j++) {
        const nextLink = linksWithFilteredChildren[j];
        if (nextLink.type === 'header') break;
        if (!nextLink.show) continue;

        // Check if nextLink is visible
        let isLinkVisible = false;
        if (nextLink.permission) {
          isLinkVisible = hasPermission(nextLink.permission);
        } else if (nextLink.children && nextLink.children.length > 0) {
          isLinkVisible = true;
        } else {
          isLinkVisible = nextLink.access ? nextLink.access.includes(userRole) : true;
        }

        // If it has children, it must have at least one visible child
        if (isLinkVisible && nextLink.children && nextLink.children.length === 0) {
          isLinkVisible = false;
        }

        if (isLinkVisible) {
          hasVisibleContent = true;
          break;
        }
      }
      
      if (hasVisibleContent) {
        result.push(link as NavigationModel);
      }
      continue;
    }

    // If it has children, it must have at least one visible child
    if (link.children && link.children.length === 0) {
      continue;
    }

    // Standard permission and role check for top-level links
    let isVisible = false;
    if (link.permission) {
      isVisible = hasPermission(link.permission);
    } else if (link.children && link.children.length > 0) {
      isVisible = true;
    } else {
      isVisible = link.access ? link.access.includes(userRole) : true;
    }

    if (isVisible) {
      result.push(link as NavigationModel);
    }
  }

  return result;
});

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

watch(
  () => route.fullPath,
  () => {
    if (isMobileMenuOpen.value) {
      isMobileMenuOpen.value = false;
    }
  }
);

const handleMobileLinkClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  const anchor = target.closest('a');
  const href = anchor?.getAttribute('href');
  if (!href) return;

  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transitions for drawer */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(-100%);
}

.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from {
  transform: translateX(100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}
</style>